// Ведущий протокола (014: T029, T030; FR-032, FR-033; research, Р6). Ведущим можно назначить
// любого действующего пользователя: сервер делает его владельцем протокола (041: поле протокола).
// Кандидаты — minutes.leads.candidates (право documents.create): только номер и ФИО, поиск от двух
// знаков на каждое нажатие с короткой задержкой. Пусто — «Никого не нашлось», ответ дольше 0,3 с —
// «Поиск…», сбой — причина, код, номер операции и «Повторить» (README «Табеля», «Состояния»).
//
// LeadSearchField — поле «Ведущий» окна «Протокол в системе» (по желанию). LeadValue — свойство
// «Ведущий» в карточке протокола: пилюля открывает окно «Назначить ведущего» (038: FR-001;
// research Р10; LEAD-01) — поповер поиска 014 убран: поиск, подтверждение смены и «Отменить» —
// в окне (features/lead). Менять ведущего может тот, кто создаёт протоколы (documents.create),
// у протокола в режиме ведения; остальным — только имя.
//
// 023 (spec, сценарий 7; FR-009; research Р5 п. 5): протокол без ведущего получает ведущим создателя,
// если ему можно быть ведущим (кандидат в ведущие — без роли «Сопровождение», сотрудник компании).
// Пока ведущий не выбран, под полем окна — «Не выбран — ведущим станете вы» (canLeadSelf).
//
// 041 (T016; FR-005; contracts/ui.md 041, «Документы»): владелец в архиве не пропадает — «Морозов
// О. С. · в архиве», пометка вторичным текстом (OwnerName); в выпадающем фильтре «Владелец» — та же
// строка текстом (ownerText), такие владельцы — в конце списка (ownerFilterOptions).
import { LOOKUP_QUERY_MAX, LOOKUP_QUERY_MIN } from '@kp/contracts';
import type { DocumentRow, MeResponse } from '@kp/contracts';
import { PropPill, SearchPickerField } from '@kp/ui';
import type { SearchPickerOption } from '@kp/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { Ref } from 'react';
import { apiQuery } from '../../api/client.ts';
import type { ErrorView } from '../../app/errors.ts';
import { useMe } from '../../shell/session.ts';
import { AssignLeadDialog } from '../lead/AssignLeadDialog.tsx';
import { canChangeOwner } from '../lead/can.ts';
import { ListLoadError, failedListText, useQueryFailure } from '../load-error.tsx';

const SEARCH_DELAY_MS = 250;

export const LEAD_CANDIDATES_FAILED = 'Не удалось загрузить список пользователей';
const TYPE_MORE = 'Введите ФИО — от двух знаков';
const NOBODY = 'Никого не нашлось. Проверьте раскладку';
/** Вариант «без ведущего» в поле окна: выбранного можно снять. */
const NO_LEAD = '';
export const LEAD_HINT = 'Станет владельцем этого протокола. Можно назначить и позже';
/** Ведущий не выбран, а создателю можно быть ведущим: сервер назначит его (023, research Р5). */
export const SELF_LEAD_HINT = 'Не выбран — владельцем станете вы';

/**
 * Создатель станет ведущим протокола без ведущего: как кандидат в ведущие на сервере
 * (leadCandidateUsers) — без роли «Сопровождение» и не внешний. Архив и блокировку проверять не
 * нужно: такой пользователь не входит в систему.
 */
export function canLeadSelf(me: Pick<MeResponse, 'roles' | 'external'>): boolean {
  return !me.external && !me.roles.some((item) => item.role === 'support');
}

/** Пометка владельца в архиве (FR-005). */
export const OWNER_ARCHIVED = 'в архиве';

/** Владелец строки «Документов»: ФИО и признак архива (DocumentRow.lead). */
export type DocumentOwner = NonNullable<DocumentRow['lead']>;

/** Владелец строкой: «Морозов О. С. · в архиве» — фильтр, строка сведений, подсказка. */
export function ownerText(owner: Pick<DocumentOwner, 'name' | 'archived'>): string {
  return owner.archived ? `${owner.name} · ${OWNER_ARCHIVED}` : owner.name;
}

/** Владелец в колонке и в карточке: пометка «· в архиве» — вторичным текстом. */
export function OwnerName({ owner }: { owner: Pick<DocumentOwner, 'name' | 'archived'> }) {
  if (!owner.archived) return <>{owner.name}</>;
  // 041 (FR-005): в узкой колонке «Владелец» обрезается ФИО, а пометка «· в архиве» остаётся
  // видна; полный текст — в подсказке.
  return (
    <span className="kp-owner" title={ownerText(owner)}>
      <span className="kp-owner-name">{owner.name}</span>
      <span className="t-muted kp-owner-mark">{` · ${OWNER_ARCHIVED}`}</span>
    </span>
  );
}

/**
 * Варианты фильтра «Владелец»: по одному на пользователя, в порядке строк; владельцы в архиве —
 * в конце списка с пометкой.
 */
export function ownerFilterOptions(
  owners: readonly DocumentOwner[],
): { userId: string; label: string }[] {
  const unique = [...new Map(owners.map((owner) => [owner.userId, owner])).values()];
  return [
    ...unique.filter((owner) => !owner.archived),
    ...unique.filter((owner) => owner.archived),
  ].map((owner) => ({ userId: owner.userId, label: ownerText(owner) }));
}

export interface LeadCandidate {
  readonly id: string;
  readonly name: string;
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface LeadCandidates {
  readonly items: readonly LeadCandidate[];
  /** Введено от двух знаков: поиск идёт или пришёл. */
  readonly ready: boolean;
  /** Ответа на введённое ещё нет. */
  readonly loading: boolean;
  /** Ответ пришёл и пуст. */
  readonly nobody: boolean;
  readonly failure: ErrorView | null;
  readonly retrying: boolean;
  readonly retry: () => void;
}

/** Кандидаты в ведущие по строке поиска (minutes.leads.candidates, до 20). */
export function useLeadCandidates(text: string, enabled = true): LeadCandidates {
  const typed = text.trim().slice(0, LOOKUP_QUERY_MAX);
  const q = useDebounced(typed, SEARCH_DELAY_MS);
  const ready = typed.length >= LOOKUP_QUERY_MIN;
  const candidates = useQuery({
    ...apiQuery('minutes.leads.candidates', { query: { q } }),
    enabled: enabled && q.length >= LOOKUP_QUERY_MIN,
    placeholderData: keepPreviousData,
  });
  const { failure, retrying, retry } = useQueryFailure(candidates, { show: ready });
  const items = ready && !failure ? (candidates.data?.items ?? []) : [];
  const answered = q === typed && candidates.isSuccess && !candidates.isFetching;
  return {
    items,
    ready,
    loading: ready && !failure && !answered && items.length === 0,
    nobody: ready && !failure && answered && items.length === 0,
    failure,
    retrying,
    retry,
  };
}

export interface LeadSearchFieldProps {
  value: SearchPickerOption | null;
  onChange: (option: SearchPickerOption | null) => void;
  error?: string;
  /** Подсказка под полем; по умолчанию — что получит ведущий (LEAD_HINT). */
  hint?: string | undefined;
  ref?: Ref<HTMLButtonElement>;
}

/** «Ведущий» окна «Протокол в системе» — по желанию (FR-032). */
export function LeadSearchField({
  value,
  onChange,
  error,
  hint = LEAD_HINT,
  ref,
}: LeadSearchFieldProps) {
  const [query, setQuery] = useState('');
  const candidates = useLeadCandidates(query);
  const { failure } = candidates;
  const options: SearchPickerOption[] = [
    ...(value ? [{ value: NO_LEAD, label: 'Без владельца' }] : []),
    ...candidates.items.map((candidate) => ({ value: candidate.id, label: candidate.name })),
  ];
  return (
    <>
      <SearchPickerField
        ref={ref}
        label="Владелец"
        optional
        hint={hint}
        placeholder="Не назначен"
        searchPlaceholder="Найти по ФИО"
        emptyText={
          !candidates.ready
            ? TYPE_MORE
            : failure
              ? failedListText(LEAD_CANDIDATES_FAILED, failure)
              : NOBODY
        }
        value={value}
        query={query}
        onQueryChange={setQuery}
        options={options}
        loading={candidates.loading}
        onChange={(option) => onChange(option.value === NO_LEAD ? null : option)}
        error={error}
      />
      {failure ? (
        <ListLoadError
          what={LEAD_CANDIDATES_FAILED}
          view={failure}
          retrying={candidates.retrying}
          onRetry={candidates.retry}
        />
      ) : null}
    </>
  );
}

/**
 * «Ведущий» в карточке протокола (014: FR-033; 038: FR-001, FR-002; LEAD-01). Тот, кто создаёт
 * протоколы, у протокола в режиме ведения: пилюля открывает окно «Назначить ведущего», после него
 * фокус — снова на пилюле; пусто — «Назначить ведущего». onOpenAccess — путь «Открыть «Доступ»»
 * из шага создания (внешний домен).
 */
export function LeadValue({
  row,
  onOpenAccess,
}: {
  row: DocumentRow;
  onOpenAccess?: (() => void) | undefined;
}) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const pillRef = useRef<HTMLElement | null>(null);
  // 040: менять владельца могут администратор и нынешний владелец этого протокола (lead/can.ts).
  const editable = row.mode === 'maintained' && canChangeOwner(me, row.lead);
  if (!editable) {
    return row.lead ? (
      <span>
        <OwnerName owner={row.lead} />
      </span>
    ) : (
      <span className="t-muted">Не назначен</span>
    );
  }
  const current = row.lead;
  return (
    <>
      <PropPill
        empty={!current}
        aria-label={current ? `Сменить владельца: ${ownerText(current)}` : 'Назначить владельца'}
        onClick={(event) => {
          pillRef.current = event.currentTarget;
          setOpen(true);
        }}
      >
        {current ? <OwnerName owner={current} /> : 'Назначить владельца'}
      </PropPill>
      <AssignLeadDialog
        protocol={{ id: row.id, number: row.number, title: row.title, lead: current }}
        open={open}
        onClose={() => setOpen(false)}
        returnFocusRef={pillRef}
        onOpenAccess={onOpenAccess}
      />
    </>
  );
}
